/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Area, AreaChart as AreaChartContainer, CartesianGrid, Legend, Tooltip, XAxis, YAxis } from 'recharts';
import CustomResponsiveContainer from '../CustomResponsiveContainer';

import AriaTooltip from './components/AriaTooltip';
import AreaActiveDot from './components/AreaActiveDot';
import AreaAxisTick from './components/AreaAxisTick';

import { StyledAreaChart } from './styled';

/*
 * Pay attention!
 * For showing tooltip you mast have tooltip property in your data object.
 * Example: [{ tooltip: "Data first", count: 7, date: 12358461 }, { tooltip: "Data second", count: 17, date: 12358461 }]
 */
const EMPTY = {};
const SIZE = {
  width: '200px',
  height: '150px'
};
/* eslint-disable react/no-unstable-nested-components */
const AreaChart = ({
  data,
  dataKey = '',
  dataKeys,
  lineType = 'monotone',
  size = SIZE,
  color = 'var(--dashboard-chart-gradiant)',
  colors,
  chartMargin = EMPTY,
  xAxisKey = '',
  yAxisKey = '',
  xAxisLabelFormatter,
  yAxisLabelFormatter,
  tooltipFormatter,
  xAxisAdditionalProps = EMPTY,
  yAxisAdditionalProps = EMPTY,
  onDotClicked,
  legendOptions,
  ChartTooltip = AriaTooltip,
  cartesianGridProps
}) => {
  const gradientID = Math.random();
  const [currentActiveTooltip, setCurrentActiveTooltip] = useState('');
  return (
    <StyledAreaChart $size={size}>
      <CustomResponsiveContainer>
        <AreaChartContainer data={data} margin={chartMargin}>
          {color ? (
            <defs>
              <linearGradient id={gradientID} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
          ) : null}
          {colors ? (
            <defs>
              {colors.map((dataColor, i) => (
                <linearGradient key={dataKeys[i]} id={dataKeys[i]} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={dataColor} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={dataColor} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
          ) : null}
          <XAxis
            padding={{ left: 19, right: 19 }}
            tickLine={false}
            axisLine={false}
            tick={
              <AreaAxisTick labelFormatter={xAxisLabelFormatter} axisTickStyle={xAxisAdditionalProps.axisTickStyle} />
            }
            dataKey={xAxisKey}
            {...xAxisAdditionalProps}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tick={
              <AreaAxisTick labelFormatter={yAxisLabelFormatter} axisTickStyle={yAxisAdditionalProps.axisTickStyle} />
            }
            dataKey={yAxisKey}
            {...yAxisAdditionalProps}
          />
          <CartesianGrid fill="var(--white)" vertical={false} strokeOpacity={0.4} {...cartesianGridProps} />
          <Tooltip
            formatter={tooltipFormatter}
            content={props => <ChartTooltip currentActive={currentActiveTooltip} {...props} />}
          />
          {legendOptions ? <Legend {...legendOptions} /> : null}
          {dataKeys
            ? dataKeys.map((dKey, i) => (
                <Area
                  key={dKey}
                  type={lineType}
                  connectNulls
                  dataKey={dKey}
                  fillOpacity={1}
                  fill={`url(#${dKey})`}
                  stroke={colors[i]}
                  strokeWidth={1}
                  cartesianGridProps={cartesianGridProps}
                  {...{
                    ...(onDotClicked && {
                      activeDot: (
                        <AreaActiveDot
                          onClick={onDotClicked}
                          data={data}
                          color={color}
                          onEnter={({ dataKey: activeDataKey }) => setCurrentActiveTooltip(activeDataKey)}
                          onLeave={() => setCurrentActiveTooltip('')}
                        />
                      )
                    })
                  }}
                />
              ))
            : null}

          {dataKey ? (
            <Area
              type={lineType}
              connectNulls
              dataKey={dataKey}
              fillOpacity={1}
              fill={`url(#${gradientID})`}
              stroke={color}
              strokeWidth={1}
              {...{
                ...(onDotClicked && {
                  activeDot: <AreaActiveDot onClick={onDotClicked} data={data} color={color} />
                })
              }}
            />
          ) : null}
        </AreaChartContainer>
      </CustomResponsiveContainer>
    </StyledAreaChart>
  );
};

AreaChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  dataKey: PropTypes.string,

  size: PropTypes.shape({
    width: PropTypes.string.isRequired,
    height: PropTypes.string.isRequired
  }),
  color: PropTypes.string,
  chartMargin: PropTypes.object,
  xAxisKey: PropTypes.string,
  yAxisKey: PropTypes.string,
  lineType: PropTypes.oneOf([
    'basis',
    'basisClosed',
    'basisOpen',
    'linear',
    'linearClosed',
    'natural',
    'monotoneX',
    'monotoneY',
    'monotone',
    'step',
    'stepBefore',
    'stepAfter'
  ]),
  xAxisLabelFormatter: PropTypes.func,
  yAxisLabelFormatter: PropTypes.func,
  tooltipFormatter: PropTypes.func,
  xAxisAdditionalProps: PropTypes.object,
  yAxisAdditionalProps: PropTypes.object,
  onDotClicked: PropTypes.func,
  dataKeys: PropTypes.arrayOf(PropTypes.string),
  colors: PropTypes.arrayOf(PropTypes.string),
  ChartTooltip: PropTypes.oneOfType([PropTypes.node, PropTypes.element, PropTypes.elementType]),
  legendOptions: PropTypes.object
};

export default AreaChart;
